import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, HStack, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverFooter, PopoverHeader, PopoverTrigger, VStack } from '@chakra-ui/react';
import * as CSS from "csstype";
import { useEffect, useState } from 'react';
import { translate } from '../../../i18n';
import { daysOfWeek } from "../../../utils/date";

// Reorder days to start with Monday (1-7, 0)
const orderedDayIndices = [1, 2, 3, 4, 5, 6, 0];
const MAX_DISPLAY_LENGTH = 20;

const formatSelectedDays = (selectedDays: number[], forFooter: boolean = false): string => {
    if (selectedDays.length === 0) return '';

    // Sort the days according to orderedDayIndices
    const sortedDays = [...selectedDays].sort((a, b) =>
        orderedDayIndices.indexOf(a) - orderedDayIndices.indexOf(b)
    );

    const groups: number[][] = [];
    let currentGroup: number[] = [sortedDays[0]];

    // Group consecutive days
    for (let i = 1; i < sortedDays.length; i++) {
        const currentDayIndex = orderedDayIndices.indexOf(sortedDays[i]);
        const prevDayIndex = orderedDayIndices.indexOf(sortedDays[i - 1]);

        if (currentDayIndex - prevDayIndex === 1) {
            currentGroup.push(sortedDays[i]);
        } else {
            groups.push(currentGroup);
            currentGroup = [sortedDays[i]];
        }
    }
    groups.push(currentGroup);

    // Format each group
    const formattedString = groups.map(group => {
        if (group.length > 2) {
            return `${translate(`dayPicker.daysOfWeek.${daysOfWeek[group[0]]}` as any).slice(0, 3)}-${translate(`dayPicker.daysOfWeek.${daysOfWeek[group[group.length - 1]]}` as any).slice(0, 3)}`;
        } else {
            return group.map(day => translate(`dayPicker.daysOfWeek.${daysOfWeek[day]}` as any).slice(0, 3)).join(', ');
        }
    }).join(', ');

    // For the button display (not footer), show count if string is too long
    if (!forFooter && formattedString.length > MAX_DISPLAY_LENGTH) {
        return `${selectedDays.length} ${translate('dayPicker.selectedDays')}`;
    }

    return formattedString;
};

type Props = {
    value: number[]; onChange: (newValues: number[]) => void;
    buttonWidth: CSS.Property.Width;
}

export default function MultiDayOfWeekPicker({ value, buttonWidth, onChange }: Props) {
    const [selectedDays, setSelectedDays] = useState(value);

    useEffect(() => {
        setSelectedDays(value);
    }, [value]);

    const handleToggle = (dayIndex: number) => {
        const newSelectedDays = selectedDays.includes(dayIndex)
            ? selectedDays.filter(day => day !== dayIndex)
            : [...selectedDays, dayIndex];
        setSelectedDays(newSelectedDays);
        onChange(newSelectedDays);
    };

    const handleSelectAll = () => {
        const allDays = daysOfWeek.map((_, index) => index);
        setSelectedDays(allDays);
        onChange(allDays);
    };

    const handleDeselectAll = () => {
        setSelectedDays([]);
        onChange([]);
    };

    return (
        <Popover>
            <PopoverTrigger>
                <Button w={buttonWidth} variant={'outline'} rightIcon={<ChevronDownIcon />}>
                    {selectedDays.length > 0
                        ? formatSelectedDays(selectedDays)
                        : translate('dayPicker.selectDays')}
                </Button>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader>{translate('dayPicker.selectDaysOfWeek')}</PopoverHeader>
                <PopoverBody>
                    <HStack spacing={2} mb={4}>
                        <Button size="sm" onClick={handleSelectAll}>
                            {translate('dayPicker.selectAll')}
                        </Button>
                        <Button size="sm" onClick={handleDeselectAll}>
                            {translate('dayPicker.deselectAll')}
                        </Button>
                    </HStack>
                    <VStack align="start">
                        {orderedDayIndices.map((dayIndex) => (
                            <Checkbox
                                key={dayIndex}
                                isChecked={selectedDays.includes(dayIndex)}
                                onChange={() => handleToggle(dayIndex)}
                            >
                                {translate(`dayPicker.daysOfWeek.${daysOfWeek[dayIndex]}` as any)}
                            </Checkbox>
                        ))}
                    </VStack>
                </PopoverBody>
                <PopoverFooter>
                    <Box>
                        {translate('dayPicker.selectedDays')}: {formatSelectedDays(selectedDays, true)}
                    </Box>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    );
}
